/**
 * Cross-platform UUIDv4 generator for sync-client code.
 *
 * Why this exists: sync-client runs in three runtimes — the browser
 * (web), the Cloudflare Worker / Durable Object (server), and React
 * Native / Hermes (mobile). The browser and the Worker both expose a
 * global `crypto.randomUUID()`, but Hermes does NOT. flashka-app
 * polyfills it (`src/lib/polyfills.js`), but only *asynchronously*
 * (behind `await import('expo-crypto')`), so any sync-client code that
 * runs before that microtask resolves sees `crypto.randomUUID` ===
 * undefined and throws "undefined is not a function" — a release-only
 * crash, because dev startup is slow enough that the polyfill usually
 * wins the race.
 *
 * This helper removes the race entirely: it derives the id synchronously
 * from `crypto.getRandomValues` (installed synchronously by msrcrypto on
 * native, long before any sync-client code runs) when `randomUUID` is
 * absent, with a final non-crypto fallback so it can never throw.
 */
type MaybeCrypto = {
  randomUUID?: () => string;
  getRandomValues?: (array: Uint8Array) => Uint8Array;
};

export function uuid(): string {
  const c = (globalThis as { crypto?: MaybeCrypto }).crypto;

  // Fast path: real Web Crypto (browser, Worker, native once polyfilled).
  if (c?.randomUUID) return c.randomUUID();

  // Native path: msrcrypto provides getRandomValues synchronously during
  // the yjs/lib0 import, well before any sync-client code can run.
  if (c?.getRandomValues) {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variant 10xx
    let s = '';
    for (let i = 0; i < 16; i++) {
      s += b[i].toString(16).padStart(2, '0');
      if (i === 3 || i === 5 || i === 7 || i === 9) s += '-';
    }
    return s;
  }

  // Last resort: non-cryptographic, but UUIDv4-shaped.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
